import { formatNameAsUrl } from '@app/finxone-web-frontend/app/lib/utils/zone-url.utils';
import {
  APP_ZONES,
  ActiveConfig,
  CommonPageConfig,
  WidgetNames,
  WorkflowConfig,
  WorkflowPageConfig,
  WorkflowWidgetConfig,
} from '@finxone-platform/shared/sys-config-types';
import { buildDynamicZoneConfig } from '@finxone-platform/shared/workflow-mapping';

export function AutoDestroy(component: any, key: string | symbol): void {
  const originalDestroy = component.ngOnDestroy;

  component.ngOnDestroy = function () {
    if (originalDestroy) {
      originalDestroy.call(this);
    }
    this[key].next();
    this[key].complete();
  };
}

// Shared error helpers for the Infrastructure tab and the Go Live wizard: backend deployment
// endpoints return sanitised, fixed-string messages on 4xx bodies, so these are safe to surface
// as-is; everything else falls back to a caller-supplied generic message.
export function backendErrorMessage(error: unknown): string | null {
  if (typeof error !== 'object' || error === null) return null;
  const message = (error as { error?: { message?: unknown } }).error?.message;
  if (typeof message === 'string' && message) return message;
  if (Array.isArray(message) && typeof message[0] === 'string' && message[0]) return message[0];
  return null;
}

export function errorStatus(error: unknown): number | null {
  if (typeof error !== 'object' || error === null) return null;
  const status = (error as { status?: unknown }).status;
  return typeof status === 'number' ? status : null;
}

export function apiErrorMessage(error: unknown, fallback: string): string {
  const message = backendErrorMessage(error);
  if (message) return message;
  if (typeof error === 'object' && error !== null) {
    const genericMessage = (error as { message?: unknown }).message;
    if (typeof genericMessage === 'string' && genericMessage) return genericMessage;
  }
  return fallback;
}

export function AppendDataBase64(base64: string) {
  return `data:image/png;base64,${base64}`;
}

export function TextToUnderscore(sourceString: string): string {
  sourceString = sourceString.trim().toLowerCase();
  sourceString = sourceString.replace(/[^A-Z0-9]+/gi, '_');
  sourceString = sourceString.replace(/_$/, '');

  return sourceString;
}

export function GeneratePassword() {
  const checkboxes = [
    'abcdefghijklmnopqrstuvwxyz',
    'ABCDEFGHIJKLMNOPWRSTUVWXYZ',
    '0123456789',
    "!@#$%^&*-_=+\\|:;',.<>/?~",
  ];

  const passwordLenght = 10;
  let dictionary: Array<string> = [];
  // Create array from chosen checkboxes
  // eslint-disable-next-line prefer-const
  dictionary = checkboxes[0]
    .split('')
    .concat(checkboxes[1].split(''), checkboxes[2].split(''), checkboxes[3].split(''));

  // Generate random password from array
  let newPassword = '';
  for (let i = 0; i < passwordLenght; i++) {
    newPassword += dictionary[Math.floor(Math.random() * dictionary.length)];
  }
  return newPassword;
}

/**
 * To remove hyphen, underscore from zone or page name.
 * @param ParamName - zone or page name
 * @param type - zone or page
 */
export function formatName(paramName: string | APP_ZONES): string {
  if (!paramName) {
    return '';
  }
  const stringWithNumbers = paramName.match(/([a-zA-Z]+)(\d+)/);
  let words = [''];
  if (stringWithNumbers) {
    words = [stringWithNumbers[1], stringWithNumbers[2]];
    if (stringWithNumbers[1].length < 2) {
      const formattedString = words.map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join('');
      return formattedString;
    }
  } else {
    if (paramName.match(/(?=[A-Z])/)) {
      words = paramName.split(/(?=[A-Z])/);
    } else {
      words = paramName.split(/[-_ ]/);
    }
  }
  const formattedString = words.map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  return formattedString;
}

export function onCreatePageConfigArray(
  pages: WorkflowPageConfig[],
  additionalPagesConfig:
    | {
        name: string;
        title: string;
        url?: string;
        widgets?: WorkflowWidgetConfig[];
        tags: string[];
        pageConfiguration: CommonPageConfig;
      }[]
    | WorkflowPageConfig[],
  workflowConfig: WorkflowConfig,
  activeConfig: ActiveConfig,
): void {
  if (pages && additionalPagesConfig) {
    const workflowPageConfigForAdditionalPages = buildDynamicZoneConfig(additionalPagesConfig);
    workflowPageConfigForAdditionalPages.forEach((pageConfig) => {
      const existingPage = pages.find((page) => page.name === pageConfig.name);
      if (!existingPage) {
        pages?.push(pageConfig);
      } else {
        addPageTagsIfNeeded(activeConfig?.activeZone, pageConfig, existingPage);
      }
    });
    pages.forEach((pagedata) => {
      addActiveZoneInMultiIcon(pagedata, workflowConfig.permissionSections, activeConfig);
    });
  }
}

export function updatePagesAsPerSelectedWorkflowManager(
  workflowPages: WorkflowPageConfig[],
  additionalPages: {
    name: string;
    title: string;
    url?: string;
    widgets?: WorkflowWidgetConfig[];
    tags: string[];
    pageConfiguration: CommonPageConfig;
  }[],
): WorkflowPageConfig[] {
  const uniqueValues = workflowPages.filter(
    (item1) => !additionalPages.some((item2) => item2.name === item1.name),
  );
  return uniqueValues;
}

function addPageTagsIfNeeded(
  activeZone: string,
  pageConfig: WorkflowPageConfig,
  existingPage: WorkflowPageConfig,
) {
  const validZones = [APP_ZONES.PAYMENT];
  if (validZones.includes(activeZone as APP_ZONES)) {
    existingPage.tags = pageConfig?.tags;
  }
}

export function addActiveZoneInMultiIcon(
  pageConfig: WorkflowPageConfig,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  permissionSections: any,
  activeConfig: ActiveConfig,
) {
  const multiiconwidgetinPage = pageConfig.widgets.filter(
    (widget) => widget.name == WidgetNames.MULTI_ICON_QUICK_LINK_WIDGET,
  );
  for (const multiiconwidget of multiiconwidgetinPage) {
    multiiconwidget.widgetConfiguration.attributes['zone'] = activeConfig['activeZone'];
    multiiconwidget.widgetConfiguration.attributes['role'] = formatNameAsUrl(activeConfig['activeRole']);
  }
  return pageConfig;
}

export function removeSpaces(inputString: string): string {
  return inputString.replace(/\s/g, '');
}

// Generic function to generate an array of objects (key value pair) from any enum
export function generateArrayFromEnum<T extends object>(enumType: T): { label: string; value: T[keyof T] }[] {
  return Object.keys(enumType).map((key) => ({
    label: formatName(enumType[key as keyof T] as string),
    value: enumType[key as keyof T],
  }));
}

/**@description Generic function to deep compare objects*/
export function deepEqual<T>(obj1: T, obj2: T): boolean {
  if (obj1 === obj2) {
    return true;
  }
  if (typeof obj1 !== 'object' || obj1 === null || typeof obj2 !== 'object' || obj2 === null) {
    return false;
  }
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  if (keys1.length !== keys2.length) {
    return false;
  }
  for (const key of keys1) {
    if (!keys2.includes(key) || !deepEqual(obj1[key as keyof T], obj2[key as keyof T])) {
      return false;
    }
  }
  return true;
}

// A function to split a css prop value & return as Number type
// e.g: it takes '10px' as input and return 10 as number
export function splitAndConvertStringToNumber(input: string): [number, string] {
  // Regular expression to capture the numeric and non-numeric parts
  const regex = /^(\d+)(\D+)$/;
  const match = input.match(regex);

  if (match) {
    const numericPart = parseInt(match[1], 10);
    const stringPart = match[2];
    return [numericPart, stringPart];
  } else {
    return [0, 'px'];
  }
}

export function deepMerge<T>(target: Partial<T>, source: T): T {
  const output: any = { ...source };

  if (target && typeof target === 'object') {
    Object.keys(target).forEach((key) => {
      const targetValue = target[key as keyof T];
      const sourceValue = source[key as keyof T];

      // If both the source and target values are objects, recursively merge them
      if (targetValue && typeof targetValue === 'object' && !Array.isArray(targetValue)) {
        output[key] = deepMerge(targetValue, sourceValue);
      } else if (targetValue !== undefined) {
        // Otherwise, use the target value if it exists
        output[key] = targetValue;
      }
    });
  }

  return output;
}

export const getEnumAsIdNameArray = <T extends Record<string, string>>(
  enumObj: T,
): { id: string; name: string }[] => {
  return Object.values(enumObj).map((value) => ({
    id: value,
    name: value.charAt(0).toUpperCase() + value.slice(1),
  }));
};
